
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: t('navigation.home'), href: '/' },
    { name: t('navigation.sugar'), href: '/acucar' },
    { name: t('navigation.coffee'), href: '/cafe' },
    { name: t('navigation.logistics'), href: '/logistica' },
    { name: t('navigation.news'), href: '/noticias' },
    { name: t('navigation.contact'), href: '/contato' }
  ];

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇧🇷', displayCode: 'PT' },
    { code: 'en', name: 'English', flag: '🇺🇸', displayCode: 'EN' },
    { code: 'es', name: 'Español', flag: '🇪🇸', displayCode: 'ES' },
    { code: 'fr', name: 'Français', flag: '🇫🇷', displayCode: 'FR' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', displayCode: 'DE' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹', displayCode: 'IT' },
    { code: 'zh', name: '中文', flag: '🇨🇳', displayCode: 'ZH' },
    { code: 'ja', name: '日本語', flag: '🇯🇵', displayCode: 'JA' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (language: typeof languages[0]) => {
    i18n.changeLanguage(language.code);
    setIsLanguageOpen(false);
  };

  return (
    <header className="bg-black shadow-lg sticky top-0 z-50">
      <nav className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white rounded-lg flex items-center justify-center shadow-lg p-0.5">
                <img 
                  src="https://static.readdy.ai/image/781a2fc213c4dd924fc481c5de3c6ab0/9fc51d80d983e85ecd697e3078ef132b.jfif"
                  alt="Lion Logo"
                  className="w-full h-full rounded object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-base sm:text-xl font-bold text-white" style={{ fontFamily: '"Poppins", sans-serif' }}>
                  Lions Trade Expert
                </span>
                <span className="text-xs text-amber-400 -mt-1 hidden sm:block">
                  Commodities Brasil
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <div className="flex items-baseline space-x-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    location.pathname === item.href
                      ? 'bg-amber-500 text-black shadow-lg'
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Desktop Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-all duration-300 cursor-pointer border border-gray-600 hover:border-amber-500 whitespace-nowrap"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-medium">{currentLanguage.displayCode}</span>
                <i className={`ri-arrow-${isLanguageOpen ? 'up' : 'down'}-s-line text-sm transition-transform duration-200`}></i>
              </button>

              {/* Language Dropdown */}
              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => handleLanguageChange(language)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer ${
                        currentLanguage.code === language.code ? 'bg-amber-50 text-amber-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{language.flag}</span>
                      <span className="text-sm font-medium">{language.name}</span>
                      {currentLanguage.code === language.code && (
                        <i className="ri-check-line text-amber-500 ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button and language selector */}
          <div className="lg:hidden flex items-center space-x-3">
            {/* Mobile Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-1 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 cursor-pointer transition-colors min-h-[44px] min-w-[44px] justify-center"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-xs font-medium">{currentLanguage.displayCode}</span>
              </button>

              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => handleLanguageChange(language)}
                      className={`w-full flex items-center space-x-2 px-3 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer min-h-[44px] ${
                        currentLanguage.code === language.code ? 'bg-amber-50 text-amber-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-base">{language.flag}</span>
                      <span className="text-sm font-medium">{language.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-3 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 cursor-pointer transition-colors min-h-[44px] min-w-[44px]"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Collapsible */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-4 space-y-1 bg-gray-900 border-t border-gray-700 rounded-b-lg">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-4 rounded-lg text-base font-medium transition-colors cursor-pointer min-h-[44px] flex items-center ${
                    location.pathname === item.href
                      ? 'bg-amber-500 text-black'
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
